import { useMemo, useState } from 'react';
import { CornerLeftUp, Search } from 'lucide-react';

import { Sheet } from '@/components/Sheet';
import { cn } from '@/lib/cn';
import { toast } from '@/lib/toast';
import { subtreeIds } from './tree';
import { useClients, useMoveTask, useTasks } from './useTasks';
import type { Task } from './types';

interface Props {
  task: Task;
  onClose: () => void;
}

/**
 * Pick a new parent for a task.
 *
 * The task itself and its own descendants are filtered out: moving a task
 * beneath its own child would cut the whole branch loose from the tree. The
 * server rejects it too: this just keeps the impossible answer off the list.
 */
export function MoveTaskSheet({ task, onClose }: Props) {
  const tasks = useTasks();
  const clients = useClients();
  const move = useMoveTask();
  const [query, setQuery] = useState('');

  const all = useMemo(() => tasks.data ?? [], [tasks.data]);
  const excluded = useMemo(() => subtreeIds(task.id, all), [task.id, all]);
  const clientName = (id: number) => clients.data?.find((c) => c.id === id)?.name ?? '';

  const candidates = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return all
      .filter((t) => !excluded.has(t.id) && t.id !== task.parent_task_id)
      .filter(
        (t) =>
          !needle ||
          t.title.toLowerCase().includes(needle) ||
          clientName(t.client_id).toLowerCase().includes(needle),
      )
      // Same client first: a subtask inherits its parent's client, so moving
      // within the client is the move that changes the least.
      .sort((a, b) => {
        const sameA = a.client_id === task.client_id ? 0 : 1;
        const sameB = b.client_id === task.client_id ? 0 : 1;
        if (sameA !== sameB) return sameA - sameB;
        return a.title.localeCompare(b.title);
      })
      .slice(0, 50);
  }, [all, excluded, query, task.client_id, task.parent_task_id, clients.data]);

  const apply = (parentId: number | null, label: string) => {
    move.mutate(
      { id: task.id, parent_task_id: parentId },
      {
        onSuccess: () => {
          toast.show(`Moved "${task.title}" ${label}`);
          onClose();
        },
      },
    );
  };

  const currentParent = all.find((t) => t.id === task.parent_task_id) ?? null;

  return (
    <Sheet title="Move task" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <p className="text-small text-text-secondary">
          {currentParent
            ? `"${task.title}" is a subtask of "${currentParent.title}".`
            : `"${task.title}" is a top-level task.`}{' '}
          Anything beneath it moves with it, and takes the new parent's client
          and project.
        </p>

        {task.parent_task_id != null && (
          <button
            type="button"
            onClick={() => apply(null, 'to the top level')}
            disabled={move.isPending}
            className={cn(
              'pressable flex min-h-touch items-center gap-2 rounded border border-border',
              'bg-surface-1 px-3 text-left text-small text-text',
              'transition-colors duration-press hover:bg-surface-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            <CornerLeftUp size={14} strokeWidth={1.5} aria-hidden className="shrink-0" />
            Make it a top-level task
          </button>
        )}

        <div className="relative">
          <Search
            size={14}
            strokeWidth={1.5}
            aria-hidden
            className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-text-tertiary"
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search tasks"
            placeholder="Search for a parent task"
            className={cn(
              'min-h-touch w-full rounded border border-border bg-surface-1 pl-7 pr-2',
              'text-small text-text outline-none focus-visible:ring-2 focus-visible:ring-ring placeholder:text-text-tertiary',
              'focus:border-accent-border',
            )}
          />
        </div>

        {candidates.length === 0 ? (
          <p className="text-small text-text-tertiary">
            {query.trim()
              ? 'No task matches that.'
              : 'No other task can take this one as a subtask.'}
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {candidates.map((candidate) => (
              <li key={candidate.id}>
                <button
                  type="button"
                  onClick={() => apply(candidate.id, `under "${candidate.title}"`)}
                  disabled={move.isPending}
                  className={cn(
                    'flex min-h-touch w-full items-center gap-2 px-2.5 text-left',
                    'transition-colors duration-press hover:bg-surface-2',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                  )}
                  style={{ paddingLeft: `${0.625 + Math.min(candidate.depth, 3) * 0.75}rem` }}
                >
                  <span className="min-w-0 flex-1 truncate text-small text-text">
                    {candidate.title}
                  </span>
                  <span
                    className={cn(
                      'shrink-0 text-label',
                      candidate.client_id === task.client_id
                        ? 'text-text-tertiary'
                        : 'text-warning',
                    )}
                  >
                    {clientName(candidate.client_id)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Sheet>
  );
}
